import { useCallback, useEffect, useState } from 'react';
import { useSession } from './session.tsx';
import { SignIn } from './screens/SignIn.tsx';
import { ProfileSetup } from './screens/ProfileSetup.tsx';
import { Home } from './screens/Home.tsx';
import { Admin } from './screens/Admin.tsx';
import { Stats } from './screens/Stats.tsx';
import { Profile } from './screens/Profile.tsx';
import { Library } from './screens/Library.tsx';
import { FeatureRequests } from './screens/FeatureRequests.tsx';
import { Lobby } from './screens/Lobby.tsx';
import { Game } from './screens/Game.tsx';
import { Spinner } from './components/ui.tsx';
import { api, ApiRequestError } from './api.ts';

type Route =
  | { name: 'home' }
  | { name: 'admin' }
  | { name: 'stats' }
  | { name: 'me' }
  | { name: 'library' }
  | { name: 'requests' }
  | { name: 'lobby'; lobbyId: string; stayInLobby?: boolean }
  | { name: 'game'; lobbyId: string };

/**
 * Routing is a path read and written by hand rather than a router library.
 *
 * There are three screens. A router would be more code than the thing it
 * routes, and the one behaviour that actually matters — a lobby URL someone can
 * paste into Discord, and the back button working — is a few lines of History
 * API either way.
 */
function routeFromLocation(): Route {
  const path = window.location.pathname;
  const gameMatch = /^\/lobby\/([A-Za-z0-9-]+)\/game$/.exec(path);
  if (gameMatch?.[1]) return { name: 'game', lobbyId: gameMatch[1] };
  const lobbyMatch = /^\/lobby\/([A-Za-z0-9-]+)$/.exec(path);
  if (lobbyMatch?.[1]) return { name: 'lobby', lobbyId: lobbyMatch[1] };
  if (path === '/admin') return { name: 'admin' };
  if (path === '/stats') return { name: 'stats' };
  if (path === '/me') return { name: 'me' };
  if (path === '/library') return { name: 'library' };
  if (path === '/requests') return { name: 'requests' };
  return { name: 'home' };
}

function pathForRoute(route: Route): string {
  if (route.name === 'game') return `/lobby/${route.lobbyId}/game`;
  if (route.name === 'lobby') return `/lobby/${route.lobbyId}`;
  if (route.name === 'admin') return '/admin';
  if (route.name === 'stats') return '/stats';
  if (route.name === 'me') return '/me';
  if (route.name === 'library') return '/library';
  if (route.name === 'requests') return '/requests';
  return '/';
}

export function App() {
  useEffect(() => {
    document.body.dataset.pageLayout = localStorage.getItem('srcbattle-home-layout') === 'wide' ? 'wide' : 'legacy';
  }, []);
  const { status, session, signIn, signInAsGuest } = useSession();
  const [route, setRoute] = useState<Route>(routeFromLocation);
  const [autoSignInTried, setAutoSignInTried] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [displayVersion, setDisplayVersion] = useState<string | null>(null);

  useEffect(() => {
    void api.siteConfig()
      .then(({ title }) => {
        document.title = title;
      })
      .catch(() => undefined);
  }, []);

  const navigate = useCallback((next: Route) => {
    setRoute(next);
    window.history.pushState({}, '', pathForRoute(next));
  }, []);

  useEffect(() => {
    const onPop = () => setRoute(routeFromLocation());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /**
   * Sign-in by link: /?code=... from the admin panel's QR, or /?guest=... from
   * a member's guest link. Either is stripped from the URL straight away, so it
   * does not linger in the address bar, in history, or in a screenshot.
   */
  useEffect(() => {
    if (status === 'loading' || autoSignInTried) return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const guest = params.get('guest');
    setAutoSignInTried(true);
    if (!code && !guest) return;

    window.history.replaceState({}, '', window.location.pathname);
    if (status !== 'signed-out') return;

    if (guest) {
      void signInAsGuest(guest).catch((caught: unknown) => {
        setLinkError(caught instanceof ApiRequestError ? caught.message : 'Could not open that link.');
      });
      return;
    }
    void signIn(code!).catch(() => {
      // Falls through to the normal sign-in form, where the member can type it.
    });
  }, [status, autoSignInTried, signIn, signInAsGuest]);

  useEffect(() => {
    if (status !== 'signed-in') {
      setDisplayVersion(null);
      return;
    }
    void api.version().then(({ version }) => setDisplayVersion(version)).catch(() => undefined);
  }, [status]);

  if (status === 'loading') {
    return <Spinner label="Checking your code" />;
  }

  if (status === 'signed-out' || !session) {
    return <SignIn notice={linkError} />;
  }

  // Kept inside the authenticated application deliberately: members can see
  // what is running without advertising build details at the sign-in gate.
  const versionBadge = displayVersion ? (
    <div className="app-version" aria-label="Application version">
      {displayVersion}
    </div>
  ) : null;

  if (session.needsProfile) {
    return <>{versionBadge}<ProfileSetup /></>;
  }

  if (route.name === 'admin' && session.isAdmin) {
    return <>{versionBadge}<Admin
      onBack={() => navigate({ name: 'home' })}
      onVersionChange={setDisplayVersion}
      onSiteTitleChange={(title) => {
        document.title = title;
      }}
    /></>;
  }

  if (route.name === 'stats') {
    return <>{versionBadge}<Stats onBack={() => navigate({ name: 'home' })} /></>;
  }

  if (route.name === 'me') {
    return <>{versionBadge}<Profile onBack={() => navigate({ name: 'home' })} /></>;
  }
  if (route.name === 'library' && !session.isGuest) {
    return <>{versionBadge}<Library onBack={() => navigate({ name: 'home' })} /></>;
  }
  if (route.name === 'requests' && !session.isGuest) {
    return <>{versionBadge}<FeatureRequests onBack={() => navigate({ name: 'home' })} /></>;
  }

  if (route.name === 'game') {
    return (
      <>{versionBadge}<Game
        key={route.lobbyId}
        lobbyId={route.lobbyId}
        onBack={() => navigate({ name: 'lobby', lobbyId: route.lobbyId, stayInLobby: true })}
      /></>
    );
  }

  if (route.name === 'lobby') {
    return (
      <>{versionBadge}<Lobby
        key={route.lobbyId}
        lobbyId={route.lobbyId}
        onLeave={() => navigate({ name: 'home' })}
        onOpenGame={() => navigate({ name: 'game', lobbyId: route.lobbyId })}
        suppressAutoOpenGame={route.stayInLobby === true}
      /></>
    );
  }

  return (
    <>{versionBadge}<Home
      onOpenAdmin={() => navigate({ name: 'admin' })}
      onOpenStats={() => navigate({ name: 'stats' })}
      onOpenProfile={() => navigate({ name: 'me' })}
      onOpenLibrary={() => navigate({ name: 'library' })}
      onOpenRequests={() => navigate({ name: 'requests' })}
      onOpenLobby={(lobbyId) => navigate({ name: 'lobby', lobbyId })}
    /></>
  );
}
